<template>
  <authorization
    permissionKey="attendance_medical_certificate"
    can="save"
    v-slot:default="{ authorization }"
  >
    <div v-if="isLoading">
      <v-skeleton-loader type="heading" transition="fade-transition" />
    </div>
    <v-container fluid v-else-if="authorization">
      <v-row dense>
        <v-col class="text-h3 gray--text">
          Atestado
          {{ lodash.get(medicalCertificate, "medicalCertificateType", "") }}
        </v-col>

        <v-col cols="auto">
          <v-btn
            color="light-blue"
            v-if="medicalCertificateType !== 'comparecimento'"
            dark
            @click="throttledSaveMedicalCertificate.flush"
            :disabled="disableEdit"
          >
            Salvar Atestado
          </v-btn>
        </v-col>

        <v-col cols="auto">
          <v-btn
            outlined
            color="light-blue"
            dark
            @click="downloadArchive"
            :disabled="!medicalCertificate || printLoading"
            :loading="printLoading"
          >
            Imprimir
          </v-btn>
        </v-col>

        <v-col cols="auto">
          <v-btn
            color="gray lighten-1"
            dark
            :to="`/prontuario/${attendance.id}/atestados`"
          >
            voltar
          </v-btn>
        </v-col>

        <v-col cols="auto">
          <EsignButton
            :esign="medicalCertificate.lastEsign"
            v-if="medicalCertificate"
          />
        </v-col>
      </v-row>

      <v-row justify="end">
        <v-col cols="auto">
          <SaveStatus
            v-if="medicalCertificateType !== 'comparecimento'"
            :saving="saving"
            :pending="pendingSave"
            :saved-at="medicalCertificate && medicalCertificate.updatedAt"
            not-saved-message="O atestado ainda não foi salvo."
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col v-if="medicalCertificateType === 'acompanhante' || medicalCertificateType === 'afastamento' || medicalCertificateType === 'detran'"  cols="6">
          <MedicalCertificateFormAcompanhanteFields
            v-if="medicalCertificateType === 'acompanhante'"
            :companionName.sync="companionName"
            :companionRelationship.sync="companionRelationship"
            :medicalLeaveStart.sync="medicalLeaveStart"
            :medicalLeaveEnd.sync="medicalLeaveEnd"
            :serviceType.sync="serviceType"
            :notes.sync="notes"
            :disableEdit="disableEdit"
            :companionRelationships="companionRelationships"
            :serviceTypes="serviceTypes"
            :errors="errors"
            @save="triggerDebouncedSave"
          />

          <MedicalCertificateFormAfastamentoFields
            v-if="medicalCertificateType === 'afastamento'"
            :medicalLeaveStart.sync="medicalLeaveStart"
            :medicalLeaveEnd.sync="medicalLeaveEnd"
            :serviceType.sync="serviceType"
            :notes.sync="notes"
            :selectedActiveDiagnosticHypotheses.sync="
              selectedActiveDiagnosticHypotheses
            "
            :selectedInactiveDiagnosticHypotheses.sync="
              selectedInactiveDiagnosticHypotheses
            "
            :disableEdit="disableEdit"
            :serviceTypes="serviceTypes"
            :activeDiagnosticHypotheses="activeDiagnosticHypotheses"
            :inactiveDiagnosticHypotheses="inactiveDiagnosticHypotheses"
            :errors="errors"
            @save="triggerDebouncedSave"
          />

          <MedicalCertificateFormDetranFields
            v-if="medicalCertificateType === 'detran'"
            :attendanceId="attendance.id"
            :notes.sync="notes"
            :detranOverrides.sync="detranOverrides"
            :disableEdit="disableEdit"
            @save="triggerDebouncedSave"
          />
        </v-col>

        <v-col>
          <v-row>
            <v-col>
              <v-textarea
                v-model="medicalCertificateText"
                outlined
                hide-details
                label="SERÁ IMPRESSO:"
                readonly
                rows="10"
                background-color="fff"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </authorization>
</template>

<script>
import COMPANION_RELATIONSHIPS from "@/graphql/medical_certificate/companion_relationships.gql";
import SAVE_MEDICAL_CERTIFICATE from "@/graphql/medical_certificate/save_medical_certificate.gql";
import MEDICAL_CERTIFICATE from "@/graphql/medical_certificate/medical_certificate.gql";
import DIAGNOSTIC_HYPOTHESES from "@/graphql/diagnostic_hypotheses.gql";

import SaveStatus from "@/components/ui/form/save_status/index.vue";
import MedicalCertificateFormAcompanhanteFields from "@/components/medical_certificates/form/acompanhante_fields.vue";
import MedicalCertificateFormAfastamentoFields from "@/components/medical_certificates/form/afastamento_fields.vue";
import MedicalCertificateFormDetranFields from "@/components/medical_certificates/form/detran_fields.vue";

const DEFAULT_DETRAN_OVERRIDES = {
  without_correction: {
    right_eye: null,
    left_eye: null,
  },
  with_correction: {
    right_eye: null,
    left_eye: null,
  },
};

export default {
  name: "MedicalCertificate",

  components: {
    MedicalCertificateFormAcompanhanteFields,
    MedicalCertificateFormAfastamentoFields,
    MedicalCertificateFormDetranFields,
    SaveStatus,
  },
  props: {
    attendance: Object,
  },
  data() {
    return {
      medicalCertificateId: "",
      forCompanion: false,
      companionName: "",
      companionRelationship: null,
      companionRelationships: [],
      diagnosticHypotheses: [],
      serviceTypes: [],
      medicalLeaveStart: "",
      medicalLeaveEnd: "",
      medicalCertificateText: "",
      serviceType: null,
      notes: "",
      selectedActiveDiagnosticHypotheses: [],
      selectedInactiveDiagnosticHypotheses: [],
      printLoading: false,
      detranOverrides: null,
      saving: false,
      pendingSave: true,
      errors: {},
    };
  },
  created() {
    this.throttledSaveMedicalCertificate = this.lodash.debounce(
      this.saveMedicalCertificate,
      20000
    );

    if (this.$route.params.medical_certificate_id) {
      if (this.$route.params.medical_certificate_id !== "novo") {
        this.medicalCertificateId = this.$route.params.medical_certificate_id;
      }
    }
  },
  mounted() {
    if (this.medicalCertificateId === "") {
      this.medicalLeaveStart = this.$moment().format("YYYY-MM-DD");
      this.medicalLeaveEnd = this.$moment().format("YYYY-MM-DD");
    }
  },
  apollo: {
    companionRelationships: {
      query: COMPANION_RELATIONSHIPS,
      update: (data) => data.medicalCertificateCompanionRelationships,
    },
    serviceTypes: {
      query: require("@/graphql/service_types.gql"),
    },
    diagnosticHypotheses: {
      query: DIAGNOSTIC_HYPOTHESES,
      variables() {
        return {
          attendanceId: this.attendance.id,
        };
      },
      result() {
        this.loadCids();
      },
    },
    medicalCertificate: {
      query: MEDICAL_CERTIFICATE,
      fetchPolicy: "no-cache",
      variables() {
        return {
          medicalCertificateId: this.medicalCertificateId,
        };
      },
      skip() {
        return this.medicalCertificateId === "";
      },
      result() {
        this.medicalCertificateText = this.medicalCertificate.text;
        this.notes = this.medicalCertificate.notes;
        this.detranOverrides = this.detranOverridesOrDefault(
          this.medicalCertificate.detranOverrides
        );

        this.forCompanion = this.medicalCertificate.forCompanion;
        if (!this.medicalCertificate.companionName === "")
          this.companionName = this.medicalCertificate.companionName;

        if (this.medicalCertificate.companionRelationship) {
          this.companionRelationship = this.lodash.find(
            this.companionRelationships,
            {
              id: this.medicalCertificate.companionRelationship.id,
            }
          );
        }

        if (this.medicalCertificate.serviceType) {
          this.serviceType = this.lodash.find(this.serviceTypes, {
            id: this.medicalCertificate.serviceType.id,
          });
        }

        this.loadCids();

        if (this.medicalCertificate.medicalLeaveStart) {
          this.medicalLeaveStart = this.$moment(
            new Date(this.medicalCertificate.medicalLeaveStart)
          ).format("YYYY-MM-DD");
        }

        if (this.medicalCertificate.medicalLeaveEnd) {
          this.medicalLeaveEnd = this.$moment(
            new Date(this.medicalCertificate.medicalLeaveEnd)
          ).format("YYYY-MM-DD");
        }
      },
    },
  },
  computed: {
    disableEdit() {
      return this.signed || this.saving;
    },
    activeDiagnosticHypotheses() {
      return this.lodash.filter(this.diagnosticHypotheses, {
        currentStatus: "active",
      });
    },
    inactiveDiagnosticHypotheses() {
      return this.lodash.filter(this.diagnosticHypotheses, {
        currentStatus: "inactive",
      });
    },
    isLoading() {
      return this.$apollo.queries.medicalCertificate.loading
    },
    cidIds() {
      let cidIds = [];

      cidIds = cidIds.concat(
        this.selectedActiveDiagnosticHypotheses.map((e) => e.cid.id),
        this.selectedInactiveDiagnosticHypotheses.map((e) => e.cid.id)
      );

      return cidIds;
    },
    medicalCertificateType() {
      return this.medicalCertificate?.medicalCertificateType;
    },
    signed() {
      if (this.medicalCertificate) {
        return this.medicalCertificate.lastEsign.status !== "editable";
      }

      return false;
    },
    valid() {
      return Object.keys(this.errors).length === 0;
    },
    isMedicalLeaveDatesRequired() {
      return ["acompanhante", "afastamento"].includes(
        this.medicalCertificateType
      );
    },
  },
  methods: {
    detranOverridesOrDefault(detranOverrides) {
      if (this.lodash.isEmpty(detranOverrides)) {
        return DEFAULT_DETRAN_OVERRIDES;
      }

      return detranOverrides;
    },
    loadCids() {
      if (!this.medicalCertificate) return;

      let cidIds = this.medicalCertificate.cids.map((e) => e.id);

      this.selectedActiveDiagnosticHypotheses = this.lodash.filter(
        this.activeDiagnosticHypotheses,
        (diagnosticHypotheses) => {
          return cidIds.includes(diagnosticHypotheses.cid.id);
        }
      );

      this.selectedInactiveDiagnosticHypotheses = this.lodash.filter(
        this.inactiveDiagnosticHypotheses,
        (diagnosticHypotheses) => {
          return cidIds.includes(diagnosticHypotheses.cid.id);
        }
      );
    },
    refreshMedicalCertificate() {
      this.$apollo.queries.medicalCertificate.refetch();
    },
    triggerDebouncedSave() {
      this.pendingSave = true;
      this.throttledSaveMedicalCertificate();
    },
    saveMedicalCertificate() {
      this.validate();

      if (!this.valid) return;
      if (this.signed) return;

      this.saving = true;

      this.apolloMutate({
        mutation: SAVE_MEDICAL_CERTIFICATE,
        variables: {
          attendanceId: this.attendance.id,
          medicalCertificateId: this.medicalCertificateId,
          forCompanion: this.forCompanion,
          companionName: this.companionName,
          companionRelationshipId: this.companionRelationship?.id,
          medicalLeaveStart: this.medicalLeaveStart,
          medicalLeaveEnd: this.medicalLeaveEnd,
          serviceTypeId: this.serviceType.id,
          notes: this.notes,
          cidIds: this.cidIds,
          detranOverrides: this.detranOverrides,
        },
      }).then(({ data }) => {
        this.saving = false;
        this.pendingSave = false;

        this.medicalCertificateId = data.saveMedicalCertificate.id;

        this.refreshMedicalCertificate();
      });
    },
    validate() {
      this.errors = {};

      if (this.isMedicalLeaveDatesRequired) {
        this.validateMedicalLeaveDates();
      }
    },
    validateMedicalLeaveDates() {
      const requiredMessage = "Este campo deve ser preenchido.";

      if (!this.medicalLeaveStart) {
        this.$set(this.errors, "medicalLeaveStart", requiredMessage);
      }

      if (!this.medicalLeaveEnd) {
        this.$set(this.errors, "medicalLeaveEnd", requiredMessage);
      }
    },
    async generatePreview() {
      await this.apolloMutate({
        mutation: require("@/graphql/esign/generate_preview.gql"),
        variables: {
          esignId: this.medicalCertificate.lastEsign.id,
          dynamicForm: true,
          examAppraisal: true,
        },
      });
      //await setTimeout(this.refreshMedicalCertificate(), 2000); não é necessário
    },
    async downloadArchive() {
      this.printLoading = true;
      await this.throttledSaveMedicalCertificate.flush();
      await this.generatePreview();
      await this.$apollo.queries.medicalCertificate.refetch();
      //await this.$store.dispatch("authentication/refreshToken");

      //if (this.medicalCertificate.lastEsign.status === "signed") {
      const file_url = this.medicalCertificate.lastEsign.fileUrl;

      await window.open(`${process.env.VUE_APP_BASE_URL}${file_url}`, "_blank");
      // } else {
      //   this.$store.dispatch("createNotification", {
      //     message: "É necessário assinar antes de imprimir.",
      //     type: "error",
      //   });
      //}

      this.printLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped></style>
